import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getGroupById,
  getGroupMembers,
  updateGroup,
  removeUserFromGroup,
  getGroupPosts,
  deleteGroupPost
} from '../../services/firestoreService';
import { handleError, handleSuccess, handleWarning } from '../../services/errorHandlingService';

const GroupAdmin = () => {
  const { id } = useParams();
  const { hasRole, currentUser } = useAuth();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const isAdmin = hasRole('admin');
  const isGroupModerator = group?.moderators?.includes(currentUser?.uid);
  const canManageGroup = isAdmin || isGroupModerator;

  useEffect(() => {
    if (id) {
      loadGroupData();
    }
  }, [id]);

  const loadGroupData = async () => {
    try {
      setLoading(true);
      const [groupData, groupMembers, groupPosts] = await Promise.all([
        getGroupById(id),
        getGroupMembers(id),
        getGroupPosts(id)
      ]);

      setGroup(groupData);
      setMembers(groupMembers || []);
      setPosts(groupPosts || []);
    } catch (error) {
      console.error('Error loading group data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to remove ${userName} from this group? This action cannot be undone.`)) {
      try {
        await removeUserFromGroup(id, userId);
        setMembers(prev => prev.filter(member => member.userId !== userId));
        handleSuccess(`${userName} has been removed from the group.`);
      } catch (error) {
        handleError(error, {
          context: {
            component: 'GroupAdmin',
            action: 'removeMember',
            groupId: id,
            userId
          }
        });
      }
    }
  };

  const handleDeletePost = async (postId, postTitle) => {
    if (window.confirm(`Are you sure you want to delete the post "${postTitle}"? This action cannot be undone.`)) {
      try {
        await deleteGroupPost(postId);
        setPosts(prev => prev.filter(post => post.id !== postId));
        handleSuccess('Post deleted successfully.');
      } catch (error) {
        handleError(error, {
          context: {
            component: 'GroupAdmin',
            action: 'deletePost',
            groupId: id,
            postId
          }
        });
      }
    }
  };

  const handleUpdateGroupSettings = async (updates) => {
    try {
      await updateGroup(id, updates);
      setGroup(prev => ({ ...prev, ...updates }));
      handleSuccess('Group settings updated successfully.');
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupAdmin',
          action: 'updateGroupSettings',
          groupId: id
        }
      });
    }
  };

  if (!canManageGroup) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600 mt-2">You don't have permission to manage this group.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Group Not Found</h2>
          <p className="text-gray-600 mt-2">The group you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Group Administration</h1>
            <p className="text-gray-600 mt-1">Managing: {group.name}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-sm ${
              group.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {group.isActive ? 'Active' : 'Inactive'}
            </span>
            {group.isPrivate && (
              <span className="px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                Private
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview' },
            { id: 'members', name: `Members (${members.length})` },
            { id: 'posts', name: `Posts (${posts.length})` },
            { id: 'settings', name: 'Settings' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-apn-blue text-apn-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Group Stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Members</span>
                <span className="font-medium">{members.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Posts</span>
                <span className="font-medium">{posts.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="font-medium">
                  {group.createdAt ? new Date(group.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Recent Activity</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• New member joined 2 hours ago</p>
              <p>• Post created 1 day ago</p>
              <p>• Member removed 3 days ago</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-apn-blue hover:bg-blue-50 rounded">
                Send Group Message
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-apn-blue hover:bg-blue-50 rounded">
                Export Member List
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded">
                Archive Group
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'members' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Group Members</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {members.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No members in this group yet.
              </div>
            ) : (
              members.map((member) => (
                <div key={member.userId} className="p-6 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700">
                        {member.name ? member.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{member.name || 'Unknown User'}</p>
                      <p className="text-sm text-gray-500">{member.email}</p>
                      <p className="text-xs text-gray-400">
                        Joined: {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      member.role === 'moderator' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {member.role || 'Member'}
                    </span>
                    <button
                      onClick={() => handleRemoveMember(member.userId, member.name)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'posts' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Group Posts</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {posts.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No posts in this group yet.
              </div>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-gray-900">{post.title}</h4>
                      <p className="text-gray-600 mt-1">{post.content}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                        <span>By: {post.authorName}</span>
                        <span>•</span>
                        <span>{post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</span>
                        <span>•</span>
                        <span>{post.likes?.length || 0} likes</span>
                        <span>•</span>
                        <span>{post.replies?.length || 0} replies</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeletePost(post.id, post.title)}
                      className="text-red-600 hover:text-red-800 text-sm ml-4"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Group Settings</h3>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const updates = {
              name: formData.get('name'),
              description: formData.get('description'),
              isPrivate: formData.get('isPrivate') === 'on',
              isActive: formData.get('isActive') === 'on'
            };
            handleUpdateGroupSettings(updates);
          }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group Name
                </label>
                <input
                  type="text"
                  name="name"
                  defaultValue={group.name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  defaultValue={group.category}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="professional">Professional</option>
                  <option value="industry">Industry Specific</option>
                  <option value="location">Location Based</option>
                  <option value="interest">Special Interest</option>
                </select>
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                rows={4}
                defaultValue={group.description}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
            
            <div className="mt-6 space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPrivate"
                  name="isPrivate"
                  defaultChecked={group.isPrivate}
                  className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                />
                <label htmlFor="isPrivate" className="ml-2 text-sm text-gray-700">
                  Private Group (requires approval to join)
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  defaultChecked={group.isActive}
                  className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active Group (visible to members)
                </label>
              </div>
            </div>
            
            <div className="mt-8">
              <button
                type="submit"
                className="bg-apn-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
              >
                Save Settings
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GroupAdmin;
